import { useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip — polished hover/focus tooltip with rich HTML content + outcome
 * colour variants. Replaces the native HTML `title` attribute on buttons
 * + chips where we want a designed feel.
 *
 * UX:
 *   - 200ms show delay (prevents flicker on quick mouse-overs)
 *   - Instant hide on mouseleave/blur
 *   - Positioned above the trigger by default, arrow pointing down
 *   - Pointer-events: none on the bubble so it never blocks the underlying
 *     trigger from receiving clicks
 *   - aria-describedby wires the tooltip text to the trigger for screen
 *     readers — pass a string-friendly fallback in `aria` if `content` is
 *     React tree
 *
 * BOW-finesse B8 (30 Jun 2026): the bubble is rendered via a PORTAL to
 * document.body with `position: fixed`, positioned from the anchor's bounding
 * rect at show-time. Previously it was an absolutely-positioned child of the
 * anchor, so any ancestor with `overflow: hidden/auto` (the beta table cells
 * `overflow:hidden`, the `.beta-table-scroll` x-scroller, panel containers)
 * clipped it — the bubble never appeared and the native `title=` fallback
 * showed instead. Portaling to the body escapes every clipping context.
 *
 * Variants colour the border + heading bar:
 *   - info     (slate)    — default
 *   - success  (green)    — Soft delete, Approve
 *   - danger   (red)      — Hard delete, Reject
 *   - warning  (amber)    — Review, suspend
 */
export type TooltipVariant = 'info' | 'success' | 'danger' | 'warning';

export interface TooltipProps {
  /** Trigger element — typically a button. */
  children: ReactNode;
  /** Rich tooltip content. Rendered inside the bubble. */
  content: ReactNode;
  /** Short title row above the body. Optional. */
  title?: string;
  /** Outcome colour variant. Defaults to info (slate). */
  variant?: TooltipVariant;
  /** Plain-text fallback for aria-describedby + reader announcement. Used
   *  when `content` is React tree (which screen readers can't summarise). */
  aria?: string;
  /** Extra class on the anchor `<span>` wrapping the trigger. Useful when
   *  the parent layout (e.g. a flex action row) needs to position the
   *  anchor — margin-left:auto on the button alone won't work because the
   *  flex child is the anchor span, not the button inside it. */
  anchorClassName?: string;
}

const SHOW_DELAY_MS = 200;

export function Tooltip({ children, content, title, variant = 'info', aria, anchorClassName }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  // Position the (portaled, fixed-position) bubble above the anchor's centre.
  // The bubble's own transform shifts it up by 100% of its height + 12px, so we
  // only need the anchor's top-centre point — no bubble measurement required.
  function computePos() {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.top });
  }

  function show() {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      computePos();
      setOpen(true);
    }, SHOW_DELAY_MS);
  }
  function hide() {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    showTimerRef.current = null;
    setOpen(false);
  }

  return (
    <span
      ref={anchorRef}
      className={`portal-tooltip-anchor${anchorClassName ? ' ' + anchorClassName : ''}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && pos && createPortal(
        <span
          role="tooltip"
          id={tooltipId}
          className={`portal-tooltip portal-tooltip-portaled portal-tooltip-${variant}`}
          aria-label={aria}
          style={{ left: pos.left, top: pos.top }}
        >
          {title && <span className="portal-tooltip-title">{title}</span>}
          <span className="portal-tooltip-body">{content}</span>
          <span className="portal-tooltip-arrow" aria-hidden="true" />
        </span>,
        document.body,
      )}
    </span>
  );
}
